
import { useState, useEffect, useCallback } from 'react';
import { useToast } from "@/hooks/use-toast";
import { db } from "@/integrations/cloudflare/client";
import { ElfScene } from "@/components/tools/scene-generator/types";

export const useSceneManager = () => {
  const [scenes, setScenes] = useState<ElfScene[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentScene, setCurrentScene] = useState<Partial<ElfScene>>({
    title: '',
    description: '',
    required_items: [''],
    setup_instructions: '',
    difficulty: 'Medium',
    time_required: ''
  });
  
  const { toast } = useToast();

  // Load scenes from the database
  const loadScenes = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await db
        .from('elf_scenes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setScenes(data as ElfScene[]);
    } catch (error: any) {
      console.error('Error loading scenes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load scenes: ' + error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadScenes();
  }, [loadScenes]);

  // Create or update scene
  const handleSubmit = async (isEditing: boolean) => {
    if (!currentScene.title || !currentScene.description || !currentScene.setup_instructions) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return false;
    }

    if (!currentScene.required_items || currentScene.required_items.length === 0 || 
        currentScene.required_items.some(item => !item.trim())) {
      toast({
        title: 'Validation Error',
        description: 'Please add at least one required item',
        variant: 'destructive',
      });
      return false;
    }

    setIsSubmitting(true);
    try {
      if (isEditing && currentScene.id) {
        // Update existing scene
        const { error } = await db
          .from('elf_scenes')
          .update({
            title: currentScene.title,
            description: currentScene.description,
            required_items: currentScene.required_items,
            setup_instructions: currentScene.setup_instructions,
            difficulty: currentScene.difficulty,
            time_required: currentScene.time_required,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentScene.id);
        
        if (error) throw error;
      } else {
        // Create new scene
        const { error } = await db
          .from('elf_scenes')
          .insert({
            title: currentScene.title,
            description: currentScene.description,
            required_items: currentScene.required_items,
            setup_instructions: currentScene.setup_instructions,
            difficulty: currentScene.difficulty || 'Medium',
            time_required: currentScene.time_required,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (error) throw error;
      }
      
      // Reload scenes
      await loadScenes();
      resetForm();
      return true;
    } catch (error: any) {
      console.error('Error saving scene:', error);
      toast({
        title: 'Error',
        description: `Failed to ${isEditing ? 'update' : 'create'} scene: ${error.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete scene
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this scene? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await db
        .from('elf_scenes')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Reload scenes
      await loadScenes();
      toast({
        title: 'Scene Deleted',
        description: 'Scene has been successfully deleted.',
      });
    } catch (error: any) {
      console.error('Error deleting scene:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete scene: ' + error.message,
        variant: 'destructive',
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentScene({
      title: '',
      description: '',
      required_items: [''],
      setup_instructions: '',
      difficulty: 'Medium',
      time_required: ''
    });
  };

  // Add new item field
  const addItemField = () => {
    setCurrentScene(prev => ({
      ...prev,
      required_items: [...(prev.required_items || []), '']
    }));
  };

  // Handle item change
  const handleItemChange = (index: number, value: string) => {
    const updatedItems = [...(currentScene.required_items || [])];
    updatedItems[index] = value;
    setCurrentScene({...currentScene, required_items: updatedItems});
  };

  // Remove an item field
  const removeItemField = (index: number) => {
    const updatedItems = [...(currentScene.required_items || [])];
    updatedItems.splice(index, 1);
    setCurrentScene({...currentScene, required_items: updatedItems});
  };

  return {
    scenes,
    isLoading,
    isSubmitting,
    currentScene,
    setCurrentScene,
    handleSubmit,
    handleDelete,
    resetForm,
    addItemField,
    handleItemChange,
    removeItemField
  };
};
